import LegalPage from "@/components/site/LegalPage";
import PageHeader from "@/components/site/PageHeader";
import CTASection from "@/components/home/CTASection";
import SEO from "@/components/site/SEO";

// Editorial static content pages: Privacy, Terms, Sitemap, 404.

export function Privacy() {
  return (
    <LegalPage
      eyebrow="Legal · Privacy"
      title="Privacy Policy"
      italicTail="What we collect and why."
      intro="How Ragi Media (trading as Ragi) collects, uses and protects the personal data of enquirers, clients and website visitors."
      effectiveDate="[Add effective date]"
      path="/privacy"
      breadcrumbs={[{ name: "Privacy Policy", path: "/privacy" }]}
      metaDescription="Ragi privacy policy: data collection, purposes, cookies, third parties, retention, rights, and international transfers under DPDP, GDPR, CCPA."
      sections={[
        {
          n: "01",
          label: "Who we are",
          body:
            "Ragi Media, a company incorporated in India (CIN: [Add CIN]) with registered office in Bengaluru, India, trading as Ragi. We act as the Data Fiduciary / Data Controller for the personal data described in this policy.",
        },
        {
          n: "02",
          label: "Data we collect",
          body:
            "Contact enquiries (name, email, phone, company, message), billing details for invoicing (address, GSTIN or tax id), payment metadata (transaction id, amount, method), project inputs shared during engagement, basic analytics (page views, referrers, device type). We do not knowingly collect data from minors.",
        },
        {
          n: "03",
          label: "Why we process it",
          body:
            "To respond to enquiries, deliver contracted services, issue compliant invoices, prevent fraud, meet statutory obligations and, with your explicit opt-in, send you occasional design and business notes.",
        },
        {
          n: "04",
          label: "Payment data",
          body:
            "We do not store card, UPI, wallet or bank credentials on our systems. Online payments are processed by Razorpay Software Pvt Ltd in a PCI DSS Level 1 environment. See our Payment Policy and Global Compliance pages for details.",
        },
        {
          n: "05",
          label: "Cookies",
          body:
            "We use minimal first-party cookies for site preferences (theme, language). We use privacy-respecting analytics (page views, sources, device type) that do not track cross-site behaviour. You can disable cookies in your browser without loss of core functionality.",
        },
        {
          n: "06",
          label: "Third parties",
          body:
            "We share data only with sub-processors necessary to deliver our services: Razorpay (payments), our accounting and email providers, and cloud hosting (India / EU regions). We do not sell personal data to any third party.",
        },
        {
          n: "07",
          label: "International transfers",
          body:
            "Where personal data of clients in the EEA or UK is transferred outside those regions, we rely on Standard Contractual Clauses with our sub-processors. Cross-border processing is otherwise conducted in accordance with the DPDP Act (India) and relevant local laws.",
        },
        {
          n: "08",
          label: "Retention",
          body:
            "Enquiry data is retained for up to 24 months from last interaction. Client billing and contractual records are retained for eight years to comply with Indian tax and companies-law requirements. Marketing subscribers are retained until they unsubscribe.",
        },
        {
          n: "09",
          label: "Your rights",
          body:
            "Access, rectification, erasure, restriction of processing, portability, objection, withdrawal of consent, and the right to lodge a complaint with your local data protection authority. See our Consent Conditions and Global Compliance pages.",
        },
        {
          n: "10",
          label: "Contact",
          body:
            "Data protection questions: privacy@ragimedia.com. Response acknowledged within one business day and resolved within thirty days.",
        },
      ]}
    />
  );
}

export function Terms() {
  return (
    <LegalPage
      eyebrow="Legal · Terms"
      title="Terms & Conditions"
      italicTail="How we work with you."
      intro="These Terms govern the engagement between Ragi Media (trading as Ragi) and every client, together with the written proposal issued for your specific project."
      effectiveDate="[Add effective date]"
      path="/terms"
      breadcrumbs={[{ name: "Terms & Conditions", path: "/terms" }]}
      metaDescription="Ragi terms of service: engagement scope, payment terms, intellectual property, confidentiality, warranties, liability, dispute resolution and governing law."
      sections={[
        {
          n: "01",
          label: "Engagement scope",
          body:
            "Each engagement is defined in a written proposal describing deliverables, timelines and fees. In case of any conflict between these Terms and the proposal, the proposal prevails for that specific engagement.",
        },
        {
          n: "02",
          label: "Fees and payment",
          body:
            "Fees, taxes, milestones and payment methods are set out in the proposal and processed under our Payment Policy. Late payments beyond thirty days attract 1.5% per month simple interest on the outstanding amount.",
        },
        {
          n: "03",
          label: "Intellectual property",
          body:
            "Intellectual property in the final delivered work transfers to the client upon full payment. Working files, drafts, unused concepts and any pre-existing frameworks or methodologies used by Ragi remain with Ragi Media.",
        },
        {
          n: "04",
          label: "Third-party assets",
          body:
            "Third-party fonts, stock imagery, plugins and software used in the delivered work are licensed to the client under the respective vendor terms. The client is responsible for any renewals or usage extensions.",
        },
        {
          n: "05",
          label: "Confidentiality",
          body:
            "Both parties agree to keep non-public information shared during the engagement confidential and to use it only for the purposes of the engagement.",
        },
        {
          n: "06",
          label: "Publicity",
          body:
            "Unless an NDA is in force, Ragi may reference the delivered work in its public portfolio, case studies, awards submissions and marketing. Confidentiality clauses in the proposal always prevail.",
        },
        {
          n: "07",
          label: "Warranties and limitation of liability",
          body:
            "We provide services with professional skill and care. Our aggregate liability under any engagement is limited to the total fees actually received under that engagement in the twelve months preceding the claim. Neither party is liable for indirect, incidental or consequential damages.",
        },
        {
          n: "08",
          label: "Cancellation and refunds",
          body:
            "Governed by our Cancellation Policy and Refund Policy.",
        },
        {
          n: "09",
          label: "Force majeure",
          body:
            "Neither party is liable for delays or non-performance caused by events beyond reasonable control, including natural disasters, government action, war, epidemics and infrastructure outages.",
        },
        {
          n: "10",
          label: "Governing law and disputes",
          body:
            "These Terms are governed by the laws of India. Disputes are subject to the exclusive jurisdiction of the courts at Bengaluru, Karnataka. Where a dispute cannot be resolved amicably within thirty days, either party may refer it to arbitration under the Arbitration and Conciliation Act, 1996, seat Bengaluru, sole arbitrator.",
        },
        {
          n: "11",
          label: "Changes to these Terms",
          body:
            "We may update these Terms from time to time. The 'Effective' date at the top of this page reflects the most recent update. Material changes to an active engagement will always be re-agreed in writing.",
        },
      ]}
    />
  );
}

export function Sitemap() {
  const groups = [
    {
      title: "Company",
      links: [
        ["/", "Home"],
        ["/about", "About"],
        ["/founder", "Founder Profile"],
        ["/contact", "Contact"],
        ["/book-meeting", "Book a meeting"],
      ],
    },
    {
      title: "Work",
      links: [
        ["/work", "Selected Work"],
        ["/case-studies", "Case Studies"],
      ],
    },
    {
      title: "Services",
      links: [
        ["/services", "All Services"],
        ["/pricing", "Indicative Pricing"],
      ],
    },
    {
      title: "Industries & Locations",
      links: [
        ["/industries", "All Industries"],
        ["/locations", "All Locations"],
      ],
    },
    {
      title: "Insights",
      links: [["/insights", "All Insights"]],
    },
    {
      title: "Legal",
      links: [
        ["/privacy", "Privacy Policy"],
        ["/terms", "Terms & Conditions"],
        ["/payment-policy", "Payment Policy"],
        ["/refund-policy", "Refund Policy"],
        ["/cancellation-policy", "Cancellation Policy"],
        ["/delivery-policy", "Delivery Policy"],
        ["/consent", "Consent Conditions"],
        ["/compliance", "Global Compliance"],
      ],
    },
  ];
  return (
    <>
      <SEO
        title="Sitemap | Ragi"
        description="Full sitemap of Ragi: company, work, services, industries, locations, insights and legal pages."
        path="/sitemap"
      />
      <PageHeader eyebrow="Sitemap" title="Everything," italicTail="in one place." />
      <section className="relative bg-ink-950 py-16 lg:py-24">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
          <div className="grid grid-cols-1 gap-10 md:grid-cols-3">
            {groups.map((g) => (
              <div key={g.title}>
                <div className="font-sans text-[11px] uppercase tracking-widest text-gold">
                  {g.title}
                </div>
                <ul className="mt-4 space-y-2 border-t border-ivory/[0.06] pt-4">
                  {g.links.map(([to, label]) => (
                    <li key={to}>
                      <a href={to} className="text-ivory hover:text-gold">
                        {label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>
      <CTASection />
    </>
  );
}

export function NotFound() {
  return (
    <>
      <SEO title="404 | Page not found | Ragi" noindex />
      <PageHeader eyebrow="404" title="Page not found." italicTail="Try our selected work." />
      <div className="mx-auto max-w-3xl px-6 py-16 text-center lg:px-10">
        <a
          href="/"
          className="inline-flex items-center gap-3 rounded-full bg-gold px-6 py-3 font-sans text-[11px] uppercase tracking-widest text-ink-950"
        >
          Return home →
        </a>
      </div>
      <CTASection />
    </>
  );
}
